import Service from '@ember/service';
import { service } from '@ember/service';
import { set } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import ENV from 'tio-employee/config/environment';
import { generateRequestId } from 'tio-common/utils/request-id';
import { timeZone } from 'tio-common/utils/date/time-zone';
import type SessionService from 'tio-employee/services/session';
import type RouterService from '@ember/routing/router-service';

type RequirementsResponse = {
  requires?: string[];
  temp_token?: string;
  post_login_redirect?: string | null;
};

export default class PostLoginService extends Service {
  @service declare session: SessionService;
  @service declare router: RouterService;
  @service declare features: import('tio-common/services/features').default;

  @tracked requires: string[] = [];
  @tracked tempToken?: string;
  @tracked verifiedAccessToken?: string;
  @tracked postLoginRedirect?: string | null;
  @tracked mfaCodeSent = false;
  completedRequirements = new Set<string>();

  initializeFromSession() {
    const data = this.session.data.authenticated as RequirementsResponse | undefined;
    if (!data) return;
    if (Array.isArray(data.requires)) {
      this.requires = data.requires;
    }
    if (typeof data.temp_token === 'string') {
      this.tempToken = data.temp_token;
    }
    if (typeof data.post_login_redirect === 'string' || data.post_login_redirect === null) {
      this.postLoginRedirect = data.post_login_redirect;
    }
  }

  get hasRequirements() {
    return this.requires.length > 0;
  }

  get nextRequirement() {
    return this.requires[0];
  }

  get nextRouteName() {
    switch (this.nextRequirement) {
      case 'mfa_sms':
        return 'post-login.mfa-sms';
      case 'terms_and_conditions':
        return 'post-login.terms';
      default:
        return null;
    }
  }

  async refreshRequirements() {
    const response = await this.request<RequirementsResponse>('/api/v2/post_login/requirements', {
      method: 'GET',
    });
    this.applyRequirementsResponse(response);
    return response;
  }

  async acceptTerms() {
    const response = await this.request<RequirementsResponse>(
      '/api/v2/post_login/terms_acceptance',
      {
        method: 'POST',
        body: JSON.stringify({}),
      }
    );
    this.completeRequirement('terms_and_conditions');
    this.applyRequirementsResponse(response);
    return response;
  }

  async complete() {
    if (this.verifiedAccessToken) {
      return this.verifiedAccessToken;
    }

    const response = await this.request<{ access_token: string }>('/api/v2/post_login/complete', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return response.access_token;
  }

  async verifyMfa(code: string) {
    const response = await this.request<{ access_token: string }>('/api/v2/mfa/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    this.verifiedAccessToken = response.access_token;
    return response.access_token;
  }

  completeRequirement(requirement: string) {
    this.completedRequirements.add(requirement);
    this.requires = this.requires.filter(
      (currentRequirement) => currentRequirement !== requirement
    );
    this.syncSessionRequirements();
  }

  async completeAuthentication(accessToken: string) {
    const wasAuthenticated = this.session.isAuthenticated;
    const redirectTarget =
      this.validRedirectTarget(this.postLoginRedirect) ||
      this.validRedirectTarget(this.session.getRedirectTarget());

    await this.session.authenticate('authenticator:saml', { access_token: accessToken });

    if (wasAuthenticated) {
      this.session.clearRedirectTarget();

      if (redirectTarget) {
        // Skipping handleAuthentication also skips its sign-in refetch of the flags
        void this.features.loadRemoteFlags(accessToken);
        this.router.transitionTo(redirectTarget);
      } else {
        this.session.handleAuthentication('authenticated.dashboard');
      }
    }

    this.clear();
  }

  async resendMfa() {
    await this.request('/api/v2/mfa/resend', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    this.mfaCodeSent = true;
  }

  async ensureMfaCodeSent() {
    if (this.mfaCodeSent) return;

    try {
      await this.resendMfa();
    } catch (error) {
      console.error('[post-login] ensureMfaCodeSent failed', error);
    }
  }

  clear() {
    this.requires = [];
    this.tempToken = undefined;
    this.verifiedAccessToken = undefined;
    this.postLoginRedirect = null;
    this.mfaCodeSent = false;
    this.completedRequirements.clear();
  }

  private applyRequirementsResponse(response: RequirementsResponse) {
    this.requires = (response.requires || []).filter(
      (requirement) => !this.completedRequirements.has(requirement)
    );
    this.postLoginRedirect = response.post_login_redirect ?? null;
    this.syncSessionRequirements();
  }

  private syncSessionRequirements() {
    const data = this.session.data.authenticated as RequirementsResponse | undefined;
    if (!data) return;
    set(this.session, 'data.authenticated', {
      ...data,
      requires: this.requires,
      post_login_redirect: this.postLoginRedirect,
    });
  }

  private validRedirectTarget(route: string | null | undefined) {
    if (!route) return null;
    if (route.includes('undefined') || !route.startsWith('/')) {
      return null;
    }

    const pathname = route.split(/[?#]/)[0] ?? '';
    const invalidPaths = ['/logged-out', '/logout', '/login'];
    const isInvalidPath =
      pathname === '/' ||
      invalidPaths.some(
        (invalidPath) => pathname === invalidPath || pathname.startsWith(`${invalidPath}/`)
      );

    if (isInvalidPath) {
      return null;
    }

    return route;
  }

  private async request<T = unknown>(path: string, init: RequestInit): Promise<T> {
    if (!this.tempToken) {
      this.initializeFromSession();
    }

    if (!this.tempToken) {
      throw new Error('Missing temp token');
    }

    const response = await fetch(`${ENV.apiHost}${path}`, {
      ...init,
      headers: {
        'x-api-key': ENV.apiKey,
        'tio-temp-token': this.tempToken,
        'Content-Type': 'application/json',
        'Time-Zone': timeZone(),
        'X-Request-Id': generateRequestId(),
      },
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error((errorBody as { error?: string }).error || response.statusText);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return (await response.json()) as T;
  }
}
